"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const hikeBenefits = [{
  id: "benefit-1",
  title: "Beginner-Friendly Interface",
  description: "Clear, step-by-step guidance designed for non-technical site owners rather than experienced search specialists."
}, {
  id: "benefit-2",
  title: "Structured Action Plans",
  description: "Breaks search optimisation into manageable weekly tasks prioritised by potential impact."
}, {
  id: "benefit-3",
  title: "Accessible Cost for Small Sites",
  description: "Provides fundamental keyword tracking and site audits without the heavy subscription fees of enterprise suites."
}, {
  id: "benefit-4",
  title: "On-Page and Local Guidance",
  description: "Practical prompts for metadata adjustments, content direction, and Google Business Profile management."
}];
export default function Hikeseo() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hikeseoendorsement" data-nav="light" className="py-16 md:py-24 bg-background text-foreground transition-colors overflow-hidden relative">
      {/* Decorative background subtle tint */}
      <div className="absolute top-1/4 left-0 size-96 bg-secondary/40 rounded-full blur-3xl -z-10 pointer-events-none" />
      <div className="absolute bottom-10 right-0 size-80 bg-accent/10 rounded-full blur-3xl -z-10 pointer-events-none" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : -16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 py-1 px-3 text-xs md:text-sm font-medium border border-border">
            DIY Software Assessment
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight">
            Why Hike SEO Is Worth Considering for In-House Optimisation
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground font-sans">
            My independent view on where Hike SEO fits for founders and small marketing teams managing search fundamentals.
          </p>
        </motion.div>

        {/* Two-Column Content & Card */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Main Narrative Column */}
          <motion.div variants={itemVariants} className="lg:col-span-7 space-y-6 text-foreground/90 font-sans leading-relaxed text-base md:text-lg">
            <p>
              When small business owners ask me how to manage core search engine optimisation in-house without the budget for a dedicated consultant, Hike SEO is often one of the first platforms I point them towards. Most industry-standard tools are designed for full-time practitioners, presenting dense dashboards and technical metrics that can quickly overwhelm non-specialists.
            </p>
            <p>
              Hike takes a more structured route by translating search data into prioritised, sequential tasks. Rather than leaving you to interpret raw keyword tables or complex crawl reports, it highlights specific changes to make on your key pages, provides straightforward on-page guidance, and helps keep your local listings consistent.
            </p>
            <p>
              It is not a replacement for comprehensive custom consultancy or complex technical migrations, but for micro-businesses, local services, and early-stage ventures, it offers a pragmatic and affordable starting point to build healthy search habits.
            </p>
            <p>
              If your goal is to learn the basics, maintain regular progress on your site, and keep software overheads manageable, it is a sensible platform to review alongside your broader digital marketing workflow.
            </p>
          </motion.div>

          {/* Endorsement Card Column */}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <Card className="border border-border bg-card shadow-md rounded-xl overflow-hidden">
              <div className="bg-muted/60 px-6 py-2.5 border-b border-border">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Platform Overview
                </span>
              </div>

              <CardHeader className="pb-4">
                <CardTitle className="text-xl md:text-2xl font-bold font-serif text-foreground">
                  Hike SEO Core Features
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground font-sans">
                  Key areas where the platform assists non-technical teams with search basics.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Benefits List */}
                <div className="space-y-4">
                  {hikeBenefits.map((benefit, i) => <div key={benefit.id} data-index={i} className="flex items-start gap-3">
                      <div className="p-1 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                        <CheckCircle2 className="size-4" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground font-sans">{benefit.title}</h4>
                        <p className="text-xs md:text-sm text-muted-foreground font-sans mt-0.5">{benefit.description}</p>
                      </div>
                    </div>)}
                </div>

                <Separator className="bg-border" />

                {/* Consultant Quote Block */}
                <div className="bg-muted/40 p-4 rounded-lg border border-border">
                  <p className="text-xs md:text-sm italic text-foreground font-sans leading-relaxed">
                    {"\"For business owners wanting clear, structured guidance without enterprise complexity, Hike provides a practical starting point for essential search tasks.\""}
                  </p>
                  <div className="flex items-center gap-3 mt-3">
                    <SafeHydrate><Avatar className="size-9 border border-border">
                      <AvatarImage src="https://mighty-wolf.10web.cloud/wp-content/uploads/2026/08/SEO-Consultant-Freelancer.png" alt="Yassine Essaadi - Freelance SEO Consultant" loading="lazy" decoding="async" />
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">
                        YE
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    <div>
                      <p className="text-xs font-bold text-foreground font-serif">Yassine Essaadi</p>
                      <p className="text-xs text-muted-foreground font-sans">Independent SEO Consultant</p>
                    </div>
                  </div>
                </div>

                {/* Single Clear Link / CTA Button */}
                <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-xs text-base rounded-lg py-5">
                  <Link to="https://hikeseo.co" newTab>
                    <span>Visit Hike SEO</span>
                    <ExternalLink className="size-4 ml-2" />
                  </Link>
                </Button>

                <p className="text-center text-xs text-muted-foreground font-sans">
                  External link to hikeseo.co
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}